import { z } from "../zod.js";

// Module 04 — Production domain events (spec 04 §6). The typed catalog of what
// slices 04-A + 04-B-1 + 04-B-2 + 04-C-1 EMIT, plus the set the module
// CONSUMES.
//
// ALL TWENTY-TWO OF §6's ROWS ARE DECLARED as of slice 04-C-5 (2026-08-07),
// and P3-I-2 (D-399 ruling 7) adds the two the scheduler reads,
// `production.work_order_reprioritized` and `production.operation_hold_changed`,
// with their emitters: twenty-four,
// each having joined WITH its emitter and never before it — slice 04-A's eight,
// `production.shift_calendar_updated` (04-B-1, in the same PR as its emit
// site), 04-B-2's two floor-truth events `production.operation_started` +
// `production.qty_reported`, 04-C-1's THREE downtime events
// (`production.downtime_logged`, `production.downtime_ended`,
// `production.downtime_reason_updated`), and 04-C-2's TWO calibration events
// (`production.calibration_recorded`, `production.calibration_drift_detected`
// — both carrying `work_center_id` with `machine_id` null, D-073), and
// 04-C-3's `production.serial_lot_captured` (2026-08-06, emitted by BOTH
// capture paths — the floor applier and e34; module 06 deliberately does NOT
// subscribe, D-085), each joining WITH the applier/cron/endpoint surface that
// emits it — and the ones left out at each step were the point.
// `module-catalog-parity.test.ts` diffs this list against
// `production-events.ts` in BOTH directions: a declared event with no emitter
// is a promise the module does not keep — a §6 row and a contract both telling
// module 03's Gantt, module 12's exception queue and module 13's dashboards to
// expect a message nothing sends. That is the exact defect the gate was built
// for (`inventory.level_drift_detected`). THE LIST BEING COMPLETE DOES NOT
// RETIRE THE GATE: it now fails in the other direction on any §6 row someone
// deletes an emitter for.
// 04-C-4 (2026-08-07) adds `production.shift_closed` and
// `production.piece_qty_captured` — the shift-close finalize and the piece-rate
// capture hook, each with its emitter.
//
// 04-C-5 (2026-08-07) adds the LAST THREE, and with them every outbound §6 row
// has an emitter in this repository: `production.defect_handoff_flagged` (the
// applier), `production.defect_handoff_disposed` (e19) and
// `production.shift_scoreboard_adjusted` (§3.7's retro-attribution, which is
// the compensating half of the one in-place mutation of a FINALIZED projection
// this module sanctions — the edit is only legitimate BECAUSE this event
// carries the deltas that caused it).
//
// NOT ONE OF THE TWENTY-TWO HAS A CONSUMER IN THIS DEPLOYMENT — modules 03, 05's
// ledger consumer, 07, 08, 09, 11, 12 and 13 do not exist (D-071), and module
// 06 deliberately does not subscribe to `production.material_issued` because
// D-065 already moved the stock synchronously in module 04's own transaction.
// They are emitted anyway, because the mutation is real and the outbox row is
// the only durable record a later module can replay from; spec 04 §10's
// amendments say so and assert the emissions in the integration suite, which
// can read the outbox. A walkthrough speaks HTTP and cannot see an outbox row.
//
// WHY `production.qty_reported` ARRIVES ONLY NOW (and stays floor-only): §6's
// payload carries `worker_id` AND `shift_instance_id`, and the §3.11 office
// path has neither by construction (§2.4's CHECK) — emitting it with a null
// shift would hand module 05's waste ledger and module 13's dashboards exactly
// the fabricated attribution §3.7 exists to make impossible. 04-B-2's floor
// capture has a shift to name, so the event fires from the batch pipeline's
// qty_reported applier — and NEVER from endpoint 34, whose office rows remain
// invisible to shift analytics by design.

const Uuid = z.guid();
/** Exact decimal text of a numeric(14,3) quantity (D-041 — never a float). */
const Qty = z.string();
const MinorMoney = z.number().int();
const Currency = z.string().length(3);

/** spec 04 §6 — consumers: 03 (pegging), 13. */
export const ProductionWorkOrderCreatedPayload = z.object({
  work_order_id: Uuid,
  wo_no: z.string(),
  product_id: Uuid,
  qty_planned: Qty,
  uom: z.string(),
  due_date: z.string().nullable(),
  planned_order_id: Uuid.nullable(),
  sales_order_line_id: Uuid.nullable(),
});

/** spec 04 §6 — consumers: 03 (schedule anchor), 06 (reservations), 12
 * (material-short exception when `shortages` is non-empty). Module 09 does NOT
 * consume it: a pegged work order's release reaches module 09 synchronously
 * through its peg port (P3-D PR-D6, D-290 ruling 1). */
export const ProductionWorkOrderReleasedPayload = z.object({
  work_order_id: Uuid,
  /** `shortfall_qty` is in `uom`, the component product's BASE unit — a BOM
   * line's own unit need only CONVERT to it (spec 01 §9.6), so the shortfall is
   * converted before it is published and carries the unit it is in (D-221). */
  shortages: z.array(
    z.object({ component_product_id: Uuid, shortfall_qty: Qty, uom: z.string().min(1).max(16) }),
  ),
});

/** spec 04 §6 — consumers: 06 (stock receipt — ALREADY posted in-txn under
 * D-065, so this is the genealogy/projection half), 11 (WIP→FG), 13. Module 09
 * is told synchronously through its peg port instead (D-290 ruling 1). */
export const ProductionWorkOrderCompletedPayload = z.object({
  work_order_id: Uuid,
  qty_good: Qty,
  qty_scrap: Qty,
  outputs: z.array(
    z.object({
      product_id: Uuid,
      kind: z.enum(["finished", "co_product", "by_product"]),
      qty: Qty,
      lot_id: Uuid.nullable(),
      cost_share_minor: MinorMoney.nullable(),
      nrv_credit_minor: MinorMoney.nullable(),
    }),
  ),
});

/** spec 04 §6 — consumers: 11 (settlement + variance posting), 13. */
export const ProductionWorkOrderClosedPayload = z.object({
  work_order_id: Uuid,
  cost_material_minor: MinorMoney,
  cost_labor_minor: MinorMoney,
  cost_overhead_minor: MinorMoney,
  cost_waste_variance_minor: MinorMoney,
  currency: Currency,
  qty_good: Qty,
});

/** spec 04 §6 — consumers: 03, 12. Module 06 is not one (retired 2026-08-04):
 * module 04 releases its own component holds in the cancel's transaction.
 * Module 09 is told synchronously through its peg port instead (D-290 ruling
 * 1). */
export const ProductionWorkOrderCancelledPayload = z.object({
  work_order_id: Uuid,
  reason: z.string(),
  qty_good_at_cancel: Qty,
});

/**
 * spec 04 §6 — consumers: 03 (live Gantt), 13. Joined by sub-step 04-B-2 (the
 * floor event spine): emitted by the batch pipeline's `op_started` applier in
 * the same transaction as the projection — including the §3.2 worker-JOIN case
 * (an op_started against an already-in_progress op opens a labor entry and
 * changes no state, and the event still fires, because a crew member joining
 * IS a floor fact). `machine_id` is null throughout P2 (D-073).
 */
export const ProductionOperationStartedPayload = z.object({
  wo_operation_id: Uuid,
  work_order_id: Uuid,
  work_center_id: Uuid,
  machine_id: Uuid.nullable(),
  worker_id: Uuid,
  shift_instance_id: Uuid,
});

/**
 * spec 04 §6 — consumers: 05 (waste ledger on scrap), 13. Joined by sub-step
 * 04-B-2: emitted by the batch pipeline's `qty_reported` applier — FLOOR
 * capture only, never the §3.11 office path (see the header note: an office
 * row has no honest shift or floor worker to name, by §2.4's CHECK).
 *
 * ONE EVENT NAME, TWO ACTS — and as of D-223 the payload SAYS WHICH. The §2.17
 * compensating correction (an office `apply_with_edit` over a
 * `qty_exceeds_remaining` review flag) rides this same name carrying SIGNED
 * DELTAS: `qty_scrap: "-59940.000"` means "the 60 000 the floor reported was
 * 60". A consumer holding an append-only ledger cannot act on a delta it
 * cannot attach to a row, and until D-223 it had to GUESS which of an
 * operation's scrap rows the delta belonged to — a guess that reverses the
 * wrong quantity the moment an operation has two.
 *
 * `operation_event_id` is the `operation_events` row this signal was emitted
 * from — the floor fact's DURABLE identity, present on both acts. A consumer
 * that keys its projection on this (rather than on the outbox row's
 * `event_id`, which is a delivery artefact) can be POINTED AT its own earlier
 * row by name.
 *
 * `corrects_operation_event_id` is non-null on the correction ONLY, and names
 * the ORIGINAL `operation_events` row whose numbers this event restates — the
 * identity the resolver already holds (`ConflictsService` reads the flagged
 * event by its `client_event_id` to compute the deltas in the first place).
 * NULL on an ordinary report: nothing is being corrected.
 */
export const ProductionQtyReportedPayload = z.object({
  wo_operation_id: Uuid,
  worker_id: Uuid,
  shift_instance_id: Uuid,
  qty_good: Qty,
  qty_scrap: Qty,
  scrap_reason_code: z.string().nullable(),
  is_rework: z.boolean(),
  operation_event_id: Uuid,
  corrects_operation_event_id: Uuid.nullable(),
});

/**
 * spec 04 §6 — consumers: 03, 07, 08 (meter advance), 13, 02 (`is_test`).
 *
 * `operation_event_id` is NULL for an office-recorded completion (§3.11): there
 * is no `operation_events` row behind it. Consumers key idempotency on
 * `wo_operation_id`, which is sound because an operation completes at most once
 * — the only re-open is §3.2's rework transition (04-C), audit-logged with its
 * `defect_handoff_id`.
 */
export const ProductionOperationCompletedPayload = z.object({
  wo_operation_id: Uuid,
  work_order_id: Uuid,
  seq_no: z.number().int(),
  work_center_id: Uuid,
  qty_good: Qty,
  qty_scrap: Qty,
  base_uom: z.string(),
  run_minutes_actual: z.number().int(),
  machine_minutes_actual: z.number().int(),
  operation_event_id: Uuid.nullable(),
  /** §3.11: which capture path produced it — "the tablet said 5980" and
   * "someone typed 5980" are never the same fact. */
  capture_source: z.enum(["floor", "office"]),
});

/** spec 04 §6 — consumers: 06 (already decremented in-txn under D-065; the
 * event is the genealogy edge + projections), 05, 11. */
export const ProductionMaterialIssuedPayload = z.object({
  work_order_id: Uuid,
  wo_operation_id: Uuid.nullable(),
  component_product_id: Uuid,
  lot_id: Uuid.nullable(),
  stock_unit_id: Uuid.nullable(),
  qty: Qty,
  issue_method: z.enum(["scan", "backflush", "manual", "remnant_match"]),
  unit_cost_minor: MinorMoney,
});

/** spec 04 §6 / §3.5 step 4 — consumers: 12, 06 (cycle-count hint). */
export const ProductionBackflushShortfallPayload = z.object({
  work_order_id: Uuid,
  wo_operation_id: Uuid,
  component_product_id: Uuid,
  /** In `uom`, the component product's BASE unit — the BOM line's requirement
   * converted, because module 06's refusal reports availability in base and a
   * shortfall is the difference between the two (D-221). */
  shortfall_qty: Qty,
  uom: z.string().min(1).max(16),
});

/**
 * spec 04 §6 — consumers: 08, 13, 18 (floor cache bump), 03 (shift-consistency
 * check). Joined by sub-step 04-B-1 (the shift-calendar spine): emitted on
 * every shift-definition create/update/deactivate — including the D-078
 * GENERAL provisioning on facility create — in the same transaction as the
 * mutation. None of the four consumers exists in this deployment (D-071); the
 * emission is asserted in the integration suite, which can read the outbox.
 */
export const ProductionShiftCalendarUpdatedPayload = z.object({
  shift_definition_id: Uuid,
  code: z.string(),
  change: z.enum(["created", "updated", "deactivated"]),
});

/**
 * spec 04 §6 — consumers: 08 (opens a breakdown when `triggers_maintenance`
 * AND `ended_at` null), 12 (schedule impact), 13. Joined by sub-step 04-C-1
 * (downtime truth): emitted by the `downtime_started` applier — AND by the
 * `op_paused` applier when the pause links a downtime reason (§5.1's
 * `downtime_reason_code`, live from this sub-step) — in the same transaction
 * as the `downtime_logs` row it describes. `machine_id` is null throughout P2
 * (D-073: the work centre is the downtime grain).
 */
export const ProductionDowntimeLoggedPayload = z.object({
  downtime_log_id: Uuid,
  work_center_id: Uuid,
  machine_id: Uuid.nullable(),
  reason_code: z.string(),
  reason_kind: z.string(),
  started_at: z.string(),
  ended_at: z.string().nullable(),
  wo_operation_id: Uuid.nullable(),
  /** §2.6: true opens a module-08 breakdown when 08 lands — pinned in the
   * integration suite until then (M04-S11's amendment). */
  triggers_maintenance: z.boolean(),
});

/** spec 04 §6 — consumers: 08 (breakdown linkage), 13. Joined by 04-C-1:
 * emitted by the `downtime_ended` applier (and by the §3.2 pause-link
 * auto-end, D-082) with the DB-generated `duration_minutes`. */
export const ProductionDowntimeEndedPayload = z.object({
  downtime_log_id: Uuid,
  work_center_id: Uuid,
  machine_id: Uuid.nullable(),
  ended_at: z.string(),
  duration_minutes: z.number().int(),
});

/** spec 04 §6 — consumers: 08, 13, 18 (floor cache bump). Joined by 04-C-1
 * with §5 e23 (the catalog CRUD) and the pack seed path — the same
 * create/update/deactivate shape as `production.shift_calendar_updated`. */
export const ProductionDowntimeReasonUpdatedPayload = z.object({
  downtime_reason_id: Uuid,
  code: z.string(),
  change: z.enum(["created", "updated", "deactivated"]),
});

/**
 * spec 04 §6 — consumers: 06 (the measure correction is ALREADY posted in-txn
 * through the D-065 remeasure seam, so this is the projection/genealogy half),
 * 13. Joined by slice 04-C-2: emitted by the `calibration_recorded` applier in
 * the same transaction as the §2.8 row, the §3.6 `calibration_variance` cost
 * entry and the module-06 measure correction it describes.
 *
 * **Payload amended 2026-08-05 (04-C-2, D-073):** carries `work_center_id`
 * with `machine_id` null — the §6 row's original `machine_id`-only payload
 * named a column nothing can fill (module 08 does not exist), and the drift
 * grain is the work centre. `machine_id` fills additively when 08 lands.
 */
export const ProductionCalibrationRecordedPayload = z.object({
  calibration_record_id: Uuid,
  work_center_id: Uuid,
  machine_id: Uuid.nullable(),
  work_order_id: Uuid,
  wo_operation_id: Uuid,
  batch_label: z.string(),
  machine_claimed_qty: Qty,
  /** The ONLY number costing/inventory/genealogy ever use (D-025). */
  measured_qty: Qty,
  uom: z.string(),
  /** Signed exact decimal, measured − claimed (DB-generated). */
  variance_qty: Qty,
  /** Signed exact decimal percentage, 4 dp (DB-generated). */
  variance_pct: z.string().nullable(),
  output_stock_unit_id: Uuid.nullable(),
});

/**
 * spec 04 §6 — consumers: 12 (exception → maintenance + facility_admin + Big
 * Boss feed), 08. Joined by slice 04-C-2: emitted by the weekly drift cron
 * (the in-process tick, §3.8) in the same transaction as the
 * `requires_measured_capture` forcing it may apply. Neither consumer exists
 * (D-071) — the emission is pinned in the integration suite.
 *
 * **Payload amended 2026-08-05 (04-C-2, D-073):** `work_center_id` with
 * `machine_id` null, same argument as `production.calibration_recorded`.
 */
export const ProductionCalibrationDriftDetectedPayload = z.object({
  work_center_id: Uuid,
  machine_id: Uuid.nullable(),
  window_days: z.number().int(),
  mean_variance_pct: z.number(),
  stddev_pct: z.number(),
  barrel_count: z.number().int(),
  /** D-025 WECO: a sustained offset (recalibrate) vs an untrustworthy meter
   * (forces `requires_measured_capture` on the work centre's routing steps). */
  classification: z.enum(["calibration_bias", "meter_unreliable"]),
  /** §3.8: Σ |variance_qty| × material cost over the window, integer minor
   * units in the facility currency. */
  est_cost_impact_minor: MinorMoney,
  currency: Currency,
});

/**
 * spec 04 §6 — the genealogy fact (§2.14, §3.3 step 8). Joined by slice 04-C-3:
 * emitted by the `serial_lot_captured` applier AND by e34's office capture
 * (D-085 pt 3), in the same transaction as the §2.14 row and any module-06
 * genealogy edge the capture implied.
 *
 * **MODULE 06 DOES NOT SUBSCRIBE (D-085 pt 1/2), and the §6 row's original
 * "06 (genealogy DAG edges)" is amended to say so.** Module 06 already writes
 * the produced-side edges synchronously inside `receiveProductionOutput`
 * (D-065); a consumer would be a SECOND, eventually-consistent writer racing a
 * transaction that already committed, and a genealogy DAG that disagrees with
 * the ledger it exists to explain is worse than one that is merely incomplete.
 * The CONSUMED-direction edge — the genuinely new information a capture adds —
 * is written through module-06-owned `GenealogyService.addEdgeInTxn` from the
 * applier's own transaction instead. The event stays because it is a real
 * domain fact and modules 12/13/18 may want it; the deliberate non-subscription
 * is recorded in `outbox-subscriber-parity.test.ts`'s UNWIRED list with that
 * reason, the same treatment `production.material_issued` already has.
 */
export const ProductionSerialLotCapturedPayload = z.object({
  capture_id: Uuid,
  work_order_id: Uuid,
  wo_operation_id: Uuid,
  direction: z.enum(["consumed", "produced"]),
  lot_id: Uuid.nullable(),
  stock_unit_id: Uuid.nullable(),
  serial_no: z.string().nullable(),
  /** Snapshotted at capture from the resolved module-06 node; null when the
   * capture named a bare engraved serial with no node behind it (§2.14's
   * D-085 pt 4 column). §2.18's override keys on it. */
  product_id: Uuid.nullable(),
  qty: Qty,
  uom: z.string(),
  /** Which hand captured it (§2.4/§2.8's grammar): a device tap, or the §3.11
   * office traveler capture on e34. */
  capture_source: z.enum(["floor", "office"]),
});

/**
 * spec 04 §6 / §3.7 — THE SHIFT CLOSE. Joined by sub-step 04-C-4, which is what
 * §3.7's 04-B-1 amendment reserved: the tick's instance half has closed
 * instances since 04-B-1, but this payload carries scoreboard rows, and until
 * `shift_scoreboards` existed emitting it would have been a §6 row this
 * deployment could not honestly fill.
 *
 * `scoreboard_rows[]` is read back FROM THE TABLE after the finalize recompute,
 * field for field — never composed from the increments the appliers maintained.
 * The two agree (that is the rebuild law), but only one of them is the
 * authority, and a consumer replaying this payload must be looking at the same
 * numbers the screen shows.
 *
 * Consumers 12 (FPY-below-threshold exception) and 13 (dashboards + digests) do
 * not exist (D-071) — the emission is pinned in the integration suite, which can
 * read the outbox; a walkthrough speaks HTTP and cannot.
 */
export const ProductionShiftClosedScoreboardRow = z.object({
  work_center_id: Uuid,
  qty_good: Qty,
  qty_scrap: Qty,
  /** Percent, 0…100, exact decimal text. Null when the denominator is 0 — a
   * shift that produced nothing has no yield, which is not the same as 0%. */
  first_pass_yield_pct: z.string().nullable(),
  downtime_minutes: z.number().int(),
  defects_handed_off: Qty,
  defects_flagged: Qty,
});

export const ProductionShiftClosedPayload = z.object({
  shift_instance_id: Uuid,
  shift_definition_code: z.string(),
  /** `YYYY-MM-DD`, the date the shift STARTS (§9.7 — the 00:30 Jan-10 event
   * belongs to the NIGHT shift dated Jan 9). */
  shift_date: z.string(),
  scoreboard_rows: z.array(ProductionShiftClosedScoreboardRow),
});

/**
 * spec 04 §6 / §2.13 — one capture per qty report that earned piece quantity.
 * Joined by sub-step 04-C-4 with the `piece_rate_entries` row it describes, in
 * the same transaction as the `qty_reported` event that spawned it.
 *
 * NO MONEY ON THE WIRE, and that is a fact about the deployment rather than a
 * redaction: §12 Q1's rate table does not exist, so `rate_minor`/`amount_minor`
 * are NULL on every row this release can write. Module 11's future pricing job
 * fills them; the QUANTITY is captured now so it has something to price.
 */
export const ProductionPieceQtyCapturedPayload = z.object({
  piece_rate_entry_id: Uuid,
  worker_id: Uuid,
  wo_operation_id: Uuid,
  qty: Qty,
});

/**
 * spec 04 §6 / §2.11 / §3.3 step 10 — a shift flagged defective WIP against an
 * earlier shift's work. Joined by sub-step 04-C-5 with the applier that writes
 * the row, in the event's own transaction.
 *
 * BOTH SHIFT IDS RIDE, and the ordering of the two names is the payload's whole
 * information content: `produced_shift_instance_id` is who made it,
 * `flagged_shift_instance_id` is who found it. A consumer must not re-derive
 * either — the producing shift is the output of a server-side walk back through
 * the operation's qty reports (§3.3 step 10) that no consumer can repeat, and a
 * device never sends it.
 *
 * Consumers 12 (route to facility_admin's disposition queue) and 13 do not
 * exist (D-071) — the emission is pinned in the integration suite.
 */
export const ProductionDefectHandoffFlaggedPayload = z.object({
  defect_handoff_id: Uuid,
  work_order_id: Uuid,
  produced_shift_instance_id: Uuid,
  flagged_shift_instance_id: Uuid,
  qty_defective: Qty,
  scrap_reason_code: z.string(),
});

/**
 * spec 04 §6 / §5 e19 — the disposition. Module 05 consumes it as the WASTE
 * LEDGER signal when `disposition = 'scrap'` (§6's own consumer column); module
 * 07 as the rework-inspection trigger when `rework`. Neither exists (D-071).
 *
 * The payload deliberately carries the PRODUCING shift, although §6 tabulates
 * only three fields: a waste-ledger consumer that had to ask module 04 which
 * shift to bill would be re-deriving the attribution this event exists to
 * announce, and the whole §3.7 mechanic is that the scrap lands on the shift
 * that MADE it rather than the one that found it.
 */
export const ProductionDefectHandoffDisposedPayload = z.object({
  defect_handoff_id: Uuid,
  work_order_id: Uuid,
  disposition: z.enum(["rework", "scrap", "accepted_as_is"]),
  qty_defective: Qty,
  scrap_reason_code: z.string(),
  produced_shift_instance_id: Uuid,
  produced_wo_operation_id: Uuid,
});

/**
 * spec 04 §6 / §3.7 — THE COMPENSATING EVENT that makes an in-place edit of a
 * finalized projection legitimate rather than a class-1 defect.
 *
 * §3.7 sanctions exactly TWO in-place mutations of applied truth in this module,
 * and the retro-attribution of `shift_scoreboards` is one of them — but ONLY
 * paired with this event. A disposition landing after the producing shift closed
 * updates a `finalized = true` row; this carries the DELTAS that were applied
 * and the yield the database recomputed AFTER them, so modules 12/13 reconcile
 * without re-processing and without re-reading the original `shift_closed`
 * payload (which is never re-emitted — §3.7 is explicit).
 *
 * `first_pass_yield_pct_after` is read BACK FROM THE ROW, never computed in the
 * service: the column is GENERATED (D-087 ruling 3), so the only honest source
 * for "what the yield is now" is the database that just derived it.
 */
export const ProductionShiftScoreboardAdjustedPayload = z.object({
  shift_instance_id: Uuid,
  work_center_id: Uuid,
  /**
   * WHY THE FLAG IS A CAUSE TOO, and §6's single-value list is amended (D-088).
   *
   * §6 tabulates `cause ('defect_disposition')`, and §3.7 describes the
   * compensating event as what pairs with "a disposition landing after the
   * producing shift closed". But the FLAG already edits a finalized row: the
   * moment a night-shift worker flags a drum, `defects_handed_off` moves onto
   * the day shift's row, which closed hours ago. That edit is the same kind of
   * act as the disposition's and needs the same durable compensation — a
   * consumer that reconciled only on `defect_disposition` would carry a
   * `defects_handed_off` it never saw move.
   *
   * Emitting it under the name `defect_disposition` would have kept §6's list
   * literally intact and lied about what happened, so the list gains a value
   * instead. It is an ENUM rather than free text because a consumer branches on
   * it, and because the next cause — the operator-run rebuild §3.7 names as the
   * exit for shifts that closed before the scoreboard existed — must arrive as
   * a named value rather than as a string somebody matched on.
   */
  cause: z.enum(["defect_flag", "defect_disposition"]),
  defect_handoff_id: Uuid,
  /** Signed exact decimal text; only the cells that MOVED are present, so a
   * consumer can tell "unchanged" from "changed to zero". */
  deltas: z.object({
    qty_scrap: Qty.optional(),
    qty_rework: Qty.optional(),
    defects_handed_off: Qty.optional(),
    defects_flagged: Qty.optional(),
  }),
  /** Null when the cell has no yield (§2.10: the denominator is 0). */
  first_pass_yield_pct_after: z.string().nullable(),
});

/**
 * spec 04 §6 — consumers: 03 (an urgent solve, spec 03 §2.19c). Added in
 * P3-I-2 (D-399 ruling 7): emitted by e8 in the PATCH's transaction when it
 * changes the `priority` or `due_date` of a released or in-progress order, or
 * of a draft carrying a `planned_order_id`, the S4 dispatch drag included.
 * `facility_id` is the order's, so the scheduler arms that facility's solve
 * without reading the order back (spec 04 §6, decided in P3-I-2).
 */
export const ProductionWorkOrderReprioritizedPayload = z.object({
  work_order_id: Uuid,
  facility_id: Uuid,
  priority_before: z.number().int().min(1).max(5),
  priority_after: z.number().int().min(1).max(5),
  due_date_before: z.string().nullable(),
  due_date_after: z.string().nullable(),
});

/**
 * spec 04 §6 — consumers: 03 (arms the solve debounce; a held operation is
 * unplaced `on_hold`). Added in P3-I-2 (D-399 ruling 7): emitted whenever an
 * operation's `qc_hold` flips, set or cleared, in the transaction that flips
 * it, from module 07's seam and from e32 alike.
 */
export const ProductionOperationHoldChangedPayload = z.object({
  wo_operation_id: Uuid,
  work_order_id: Uuid,
  facility_id: Uuid,
  qc_hold: z.boolean(),
});

export const productionProducerEvents = {
  "production.work_order_created": {
    payload: ProductionWorkOrderCreatedPayload,
    consumers: ["03", "13"],
  },
  "production.work_order_released": {
    payload: ProductionWorkOrderReleasedPayload,
    consumers: ["03", "06", "09", "12"],
  },
  "production.work_order_completed": {
    payload: ProductionWorkOrderCompletedPayload,
    // "05" added by D-103 (module 05 §3.6 amendment): by/co-product outputs
    // ride THIS payload's `outputs[]`, not `qty_reported` — 05-D's PVC batch
    // birth consumes it (unwired until then; the UNWIRED list carries it).
    // "03" since P3-I-2: the scheduler arms its solve (spec 03 §6).
    consumers: ["03", "05", "06", "09", "11", "13"],
  },
  "production.work_order_closed": {
    payload: ProductionWorkOrderClosedPayload,
    consumers: ["11", "13"],
  },
  "production.work_order_cancelled": {
    payload: ProductionWorkOrderCancelledPayload,
    consumers: ["03", "06", "09", "12"],
  },
  "production.operation_started": {
    payload: ProductionOperationStartedPayload,
    consumers: ["03", "13"],
  },
  "production.operation_completed": {
    payload: ProductionOperationCompletedPayload,
    consumers: ["02", "03", "07", "08", "13"],
  },
  "production.qty_reported": {
    payload: ProductionQtyReportedPayload,
    consumers: ["05", "13"],
  },
  "production.material_issued": {
    payload: ProductionMaterialIssuedPayload,
    consumers: ["05", "06", "11"],
  },
  "production.backflush_shortfall": {
    payload: ProductionBackflushShortfallPayload,
    consumers: ["06", "12"],
  },
  "production.shift_calendar_updated": {
    payload: ProductionShiftCalendarUpdatedPayload,
    consumers: ["03", "08", "13", "18"],
  },
  "production.downtime_logged": {
    payload: ProductionDowntimeLoggedPayload,
    consumers: ["08", "12", "13"],
  },
  "production.downtime_ended": {
    payload: ProductionDowntimeEndedPayload,
    consumers: ["08", "13"],
  },
  "production.downtime_reason_updated": {
    payload: ProductionDowntimeReasonUpdatedPayload,
    consumers: ["08", "13", "18"],
  },
  "production.calibration_recorded": {
    payload: ProductionCalibrationRecordedPayload,
    consumers: ["06", "13"],
  },
  "production.calibration_drift_detected": {
    payload: ProductionCalibrationDriftDetectedPayload,
    consumers: ["08", "12"],
  },
  // D-085: `06` LEAVES this consumer list. It was the only name on it, and it
  // was wrong — see the payload's note. The remaining names are the modules a
  // genealogy fact is genuinely useful to and that do not already have it.
  "production.serial_lot_captured": {
    payload: ProductionSerialLotCapturedPayload,
    consumers: ["12", "13", "18"],
  },
  // 04-C-4 — the two events whose EMITTERS ship in the same change. The third
  // name §6 gives this area, `production.shift_scoreboard_adjusted`, joined in
  // 04-C-5 immediately below, WITH its only emitter: the §3.7 retro-attribution
  // a defect DISPOSITION triggers. A declared event with no emitter is exactly
  // what `module-catalog-parity` fails in both directions, and the reason it
  // exists (`inventory.level_drift_detected`).
  "production.shift_closed": {
    payload: ProductionShiftClosedPayload,
    consumers: ["12", "13"],
  },
  "production.piece_qty_captured": {
    payload: ProductionPieceQtyCapturedPayload,
    consumers: ["11", "13"],
  },
  // 04-C-5 — the last three of §6's twenty-two rows, all emitted by the defect
  // hand-off path. With these the module's producer catalog is COMPLETE: every
  // §6 outbound row now has an emitter in this repository.
  "production.defect_handoff_flagged": {
    payload: ProductionDefectHandoffFlaggedPayload,
    consumers: ["12", "13"],
  },
  "production.defect_handoff_disposed": {
    payload: ProductionDefectHandoffDisposedPayload,
    consumers: ["05", "07", "13"],
  },
  "production.shift_scoreboard_adjusted": {
    payload: ProductionShiftScoreboardAdjustedPayload,
    consumers: ["12", "13"],
  },
  // P3-I-2 (D-399 ruling 7), the two events module 03's scheduler reads,
  // each naming "03" with the scheduler's subscriber.
  "production.work_order_reprioritized": {
    payload: ProductionWorkOrderReprioritizedPayload,
    consumers: ["03"],
  },
  "production.operation_hold_changed": {
    payload: ProductionOperationHoldChangedPayload,
    consumers: ["03"],
  },
} as const;

export type ProductionEventName = keyof typeof productionProducerEvents;

export const productionProducerEventNames = Object.keys(
  productionProducerEvents,
) as ProductionEventName[];

/**
 * Events module 04 CONSUMES (spec 04 §6 "Consumed by this module"). EMPTY in
 * slice 04-A.
 *
 * §6 names four — `quality.inspection_failed`, `quality.inspection_passed`,
 * `quality.hold_released` and `maintenance.breakdown_resolved` — and all four
 * belong to modules 07 and 08, which do not exist (D-071). Their consumers are
 * the `qc_hold` guard and the downtime auto-end, both of which ship in 04-C
 * *with the column and the table they act on*. Declaring a subscription this
 * deployment does not make would fail the live-registry parity gate in both
 * directions, which is exactly what it is for.
 */
export const productionConsumedEventNames = [] as const;
